const APPROVAL_FIELDS = [
  "approver", "pending_approval_by", "approved_at", "submitted_by", "submission_message",
  "submitted_at", "approval_or_rejection_message", "approved_or_rejected_by", "approval_or_rejection_at"
];

const clone = (value) => {
  if (value === undefined) return value;
  return JSON.parse(JSON.stringify(value));
};

const normalizeCategories = (apiPlan = {}) => {
  if (Array.isArray(apiPlan.categories)) {
    return apiPlan.categories
      .filter((category) => category && category.id !== undefined && category.id !== null)
      .map((category) => ({
        value: Number(category.id),
        label: category.category_name || category.name || `${category.id}`
      }));
  }

  if (apiPlan.category && apiPlan.category.id !== undefined && apiPlan.category.id !== null) {
    return [{
      value: Number(apiPlan.category.id),
      label: apiPlan.category.category_name || apiPlan.category.name || `${apiPlan.category.id}`
    }];
  }

  return [];
};

const serializeCategories = (categories = []) => (Array.isArray(categories) ? categories
  .filter((category) => category && category.value !== undefined && category.value !== null)
  .map((category) => ({
    id: Number(category.value),
    category_name: category.label
  })) : []);

const normalizeMethodItemType = (part = {}) => {
  const source = part.method_item_type;
  const value = part.method_item_type_id;
  if (value === undefined || value === null || value === "") return source || null;

  const label = typeof part.method_item_type === "string"
    ? part.method_item_type
    : (source && typeof source === "object" ? (source.label || source.name) : "");

  return {
    label: label || `${value}`,
    value
  };
};

export const normalizeEmergencyPlan = (apiPlan = {}) => {
  const sourceSections = Array.isArray(apiPlan.sections) ? apiPlan.sections : [];
  const sections = sourceSections.reduce((result, section) => {
    if (!section) return result;
    const key = section.id || section.key;
    if (key) {
      const data = { ...(section.data || {}), toggle: section.data && section.data.toggle !== undefined ? Number(section.data.toggle) : 1 };
      if (key === "rescue_equipment_staging") {
        ["rescue_equipment", "rescue_ppe", "licences_competencies"].forEach((partKey) => {
          if (data[partKey]) {
            data[partKey] = {
              ...data[partKey],
              method_item_type: normalizeMethodItemType(data[partKey])
            };
          }
        });
      }
      result[key] = { ...clone(section), data };
    }
    return result;
  }, {});
  const sectionOrder = Array.isArray(apiPlan.section_order)
    ? [...apiPlan.section_order]
    : sourceSections.map((section) => section && (section.id || section.key)).filter(Boolean);
  const procedures = (Array.isArray(apiPlan.linked_objects)
    ? clone(apiPlan.linked_objects)
    : (Array.isArray(apiPlan.procedures)
      ? clone(apiPlan.procedures)
      : (Array.isArray(apiPlan.linked_documents)
        ? clone(apiPlan.linked_documents)
        : []))).map((procedure) => ({
    ...procedure,
    checked: procedure.checked === undefined ? true : Boolean(procedure.checked)
  }));
  const procedureOrder = procedures
    .slice()
    .sort((a, b) => Number(a.display_order || 0) - Number(b.display_order || 0))
    .map((procedure) => procedure.id !== undefined ? procedure.id : (procedure.emergency_procedure_id !== undefined ? procedure.emergency_procedure_id : procedure.procedure_id));
  const proceduresSelection = procedures.reduce((result, procedure) => {
    const id = procedure.id !== undefined
      ? procedure.id
      : (procedure.emergency_procedure_id !== undefined ? procedure.emergency_procedure_id : procedure.procedure_id);
    if (id !== undefined) result[id] = true;
    return result;
  }, {});

  return {
    ...clone(apiPlan),
    plan_id: apiPlan.plan_id !== undefined
      ? apiPlan.plan_id
      : (apiPlan.source && apiPlan.source.plan_code) || apiPlan.id,
    title: apiPlan.title !== undefined ? apiPlan.title : (apiPlan.plan_name || ""),
    categories: normalizeCategories(apiPlan),
    toggle: apiPlan.toggle === undefined ? 1 : Number(apiPlan.toggle),
    sections,
    section_order: sectionOrder,
    procedures,
    procedure_order: procedureOrder,
    procedures_selection: proceduresSelection
  };
};

export const normalizeEmergencyPlans = (plans = []) => (Array.isArray(plans) ? plans : []).map(normalizeEmergencyPlan);

const serializeRescueEquipmentPart = (part = {}, methodItemTypeGroups = []) => {
  const typeId = part.method_item_type_id !== undefined
    ? part.method_item_type_id
    : (part.method_item_type && part.method_item_type.value);
  const typeLabel = typeof part.method_item_type === "string"
    ? part.method_item_type
    : (part.method_item_type && part.method_item_type.label) || "";
  const selectedItems = part.selected_items && typeof part.selected_items === "object"
    ? part.selected_items
    : {};
  const group = methodItemTypeGroups.find((itemGroup) => `${itemGroup.method_item_type_id}` === `${typeId}`);
  const availableItems = group && Array.isArray(group.method_items) ? group.method_items : [];
  const currentItems = Array.isArray(part.method_items) ? part.method_items : [];

  return {
    method_item_type_id: typeId,
    method_item_type: typeLabel,
    method_items: Object.keys(selectedItems)
      .filter((itemId) => selectedItems[itemId])
      .map((itemId) => {
        const currentItem = currentItems.find((candidate) => `${candidate.id}` === `${itemId}`);
        const item = currentItem || availableItems.find((candidate) => `${candidate.id}` === `${itemId}`);
        const isManual = currentItem && (currentItem.isNew === true || currentItem.is_new === 1);
        return {
          id: item ? item.id : itemId,
          method_item: item ? (item.method_item || item.name || "") : "",
          ...(isManual ? { isNew: true, is_new: 1 } : {})
        };
      })
  };
};

export const serializeEmergencyPlan = (values = {}, originalPlan = {}, methodItemTypeGroups = []) => {
  const source = clone(originalPlan) || {};
  const result = { ...source };
  APPROVAL_FIELDS.forEach((field) => { delete result[field]; });
  delete result.plan_id;
  delete result.document_number;
  delete result.source;
  result.plan_name = values.plan_name !== undefined && values.plan_name !== null
    ? values.plan_name
    : (values.title !== undefined ? values.title : source.plan_name);
  if (values.authorisation_note !== undefined) {
    result.authorisation_note = values.authorisation_note;
  }
  const categories = Array.isArray(values.categories)
    ? serializeCategories(values.categories)
    : serializeCategories(normalizeCategories(source));
  delete result.category;
  delete result.category_id;
  result.categories = categories;
  result.toggle = values.toggle === undefined ? 1 : Number(values.toggle);
  const map = values.sections && typeof values.sections === "object" ? values.sections : {};
  const sourceSections = Array.isArray(source.sections)
    ? source.sections
    : (source.sections && typeof source.sections === "object" ? Object.keys(source.sections).map((key) => source.sections[key]) : []);
  const sourceSectionMap = sourceSections.reduce((resultMap, section) => {
    const key = section && (section.id || section.key);
    if (key) resultMap[key] = section;
    return resultMap;
  }, {});
  const order = Array.isArray(values.section_order)
    ? values.section_order
    : (Array.isArray(source.section_order) ? source.section_order : Object.keys(map));
  const sectionKeys = [...order, ...Object.keys(map), ...Object.keys(sourceSectionMap)]
    .filter((key, index, keys) => key && keys.indexOf(key) === index);
  result.section_order = sectionKeys;
  result.sections = sectionKeys.map((key) => {
    const section = { ...(clone(sourceSectionMap[key]) || {}), ...(clone(map[key]) || {}) };
    const data = {
      ...((clone(sourceSectionMap[key] && sourceSectionMap[key].data) || {})),
      ...((clone(map[key] && map[key].data) || {}))
    };
    if (key === "rescue_equipment_staging") {
      return {
        id: "rescue_equipment_staging",
        type: "rescue_equipment_staging",
        display_name: section.display_name
          || sourceSectionMap[key].display_name
          || "Rescue Equipment & Staging",
        data: {
          licences_competencies: serializeRescueEquipmentPart(data.licences_competencies, methodItemTypeGroups),
          rescue_ppe: serializeRescueEquipmentPart(data.rescue_ppe, methodItemTypeGroups),
          rescue_equipment: serializeRescueEquipmentPart(data.rescue_equipment, methodItemTypeGroups),
          toggle: data.toggle === undefined ? 1 : Number(data.toggle)
        },
        toggle: section.toggle === undefined ? 1 : Number(section.toggle)
      };
    }
    return { ...section, id: section.id || key, data: { ...data, toggle: data.toggle === undefined ? 1 : Number(data.toggle) } };
  });
  const sourceProcedures = Array.isArray(values.procedures)
    ? values.procedures
    : (Array.isArray(source.procedures) ? source.procedures : []);
  const procedureOrder = Array.isArray(values.procedure_order)
    ? values.procedure_order
    : sourceProcedures.map((item) => item.id !== undefined ? item.id : (item.emergency_procedure_id !== undefined ? item.emergency_procedure_id : item.procedure_id));
  const selection = values.procedures_selection && typeof values.procedures_selection === "object"
    ? values.procedures_selection
    : null;
  const isSelected = (procedure) => {
    const id = procedure.id !== undefined
      ? procedure.id
      : (procedure.emergency_procedure_id !== undefined ? procedure.emergency_procedure_id : procedure.procedure_id);
    if (selection) return selection[id] === true || selection[id] === 1 || selection[id] === "1";
    return procedure.checked !== false;
  };
  const orderedProcedures = procedureOrder.map((id) => (
    sourceProcedures.find((item) => `${item.id !== undefined ? item.id : (item.emergency_procedure_id !== undefined ? item.emergency_procedure_id : item.procedure_id)}` === `${id}`)
  )).filter(Boolean);
  const linkedDocuments = orderedProcedures.filter(isSelected).map((procedure, index) => ({
    ...clone(procedure),
    display_order: index + 1
  }));
  result.linked_documents = linkedDocuments;
  // result.procedures = linkedDocuments;
  delete result.linked_objects;
  delete result.procedures;
  delete result.procedures_selection;
  delete result.procedure_order;
  delete result.title;
  return result;
};

export default normalizeEmergencyPlan;
export const normalizePlan = normalizeEmergencyPlan;
export const serializePlan = serializeEmergencyPlan;
